import React, { useEffect } from 'react';

/**
 * Live Chat Widget - Tawk.to integration
 * FREE forever - just create account at https://www.tawk.to and paste the widget ID + property ID
 * 
 * Setup:
 * 1. Go to https://www.tawk.to and sign up (free)
 * 2. Create a property (your website kaya-group.eu)
 * 3. Get your Widget code - it has this format:
 *    https://embed.tawk.to/{PROPERTY_ID}/{WIDGET_ID}
 * 4. Set env vars in /app/frontend/.env:
 *    REACT_APP_TAWK_PROPERTY_ID=your_property_id
 *    REACT_APP_TAWK_WIDGET_ID=your_widget_id
 * 5. Rebuild: yarn build
 */
export const LiveChatWidget = () => {
  useEffect(() => {
    const propertyId = process.env.REACT_APP_TAWK_PROPERTY_ID;
    const widgetId = process.env.REACT_APP_TAWK_WIDGET_ID;

    // Only load if credentials are configured
    if (!propertyId || !widgetId) {
      console.log('[LiveChat] Tawk.to not configured. Set REACT_APP_TAWK_PROPERTY_ID and REACT_APP_TAWK_WIDGET_ID in .env');
      return;
    }

    // Load Tawk.to script
    const s1 = document.createElement('script');
    s1.async = true;
    s1.src = `https://embed.tawk.to/${propertyId}/${widgetId}`;
    s1.charset = 'UTF-8';
    s1.setAttribute('crossorigin', '*');
    document.body.appendChild(s1);

    window.Tawk_API = window.Tawk_API || {};
    window.Tawk_LoadStart = new Date();

    // Customize appearance to match brand
    window.Tawk_API.onLoad = function() {
      if (window.Tawk_API.setAttributes) {
        window.Tawk_API.setAttributes({
          brand: 'Kaya Property Management'
        }, function() {});
      }
    };

    return () => {
      // Cleanup
      try { document.body.removeChild(s1); } catch (e) {}
    };
  }, []);

  // Fallback: Show a floating WhatsApp button if Tawk.to not configured
  const propertyId = process.env.REACT_APP_TAWK_PROPERTY_ID;
  const widgetId = process.env.REACT_APP_TAWK_WIDGET_ID;
  const showFallback = !propertyId || !widgetId;

  if (!showFallback) return null;

  return (
    <a
      href="https://wa.me/355672000026?text=Përshëndetje!%20Kam%20interes%20për%20pronat%20tuaja."
      target="_blank"
      rel="noopener noreferrer"
      className="fixed bottom-6 right-6 z-40 group"
      data-testid="whatsapp-floating"
      aria-label="Chat on WhatsApp"
    >
      {/* Pulsing ring */}
      <span className="absolute inset-0 rounded-full bg-[#25D366]/40 animate-ping" />
      
      {/* Main button */}
      <div className="relative w-16 h-16 rounded-full bg-gradient-to-br from-[#25D366] to-[#128C7E] flex items-center justify-center shadow-2xl shadow-[#25D366]/50 group-hover:scale-110 transition-transform duration-300 border-4 border-white/10">
        <svg viewBox="0 0 24 24" className="w-8 h-8 fill-white" xmlns="http://www.w3.org/2000/svg">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
        </svg>
        
        {/* Small notification dot */}
        <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full border-2 border-white text-[10px] text-white flex items-center justify-center font-bold">1</span>
      </div>
      
      {/* Hover tooltip */}
      <div className="absolute right-full mr-3 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none whitespace-nowrap">
        <div className="bg-black/90 backdrop-blur-xl text-white text-sm font-semibold px-4 py-2 rounded-xl border border-white/10 shadow-2xl">
          Bisedo në WhatsApp
        </div>
      </div>
    </a>
  );
};
